// src/components/main/produzione/assegnazioniGantt/GanttMacchinaRow.tsx

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import { RootState } from "redux/store";
import { AssegnazioneGantt } from "./assegnazioniGanttTypes";
import {Macchina, Tecnica} from "components/anagrafiche/anagraficheSlice";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import GanttBar, { GanttBarDragItem, getAcceptedTypes } from "./GanttBar";
import GanttDropCell from "./GanttDropCell";

// ============================================
// INTERFACES
// ============================================

interface GanttMacchinaRowProps {
    macchina: Macchina;
    dataDa: DateTime;
    numeroSettimane: 1 | 2 | 3 | 4 | 6;
    ordineLavoroSelezionatoId?: number;
    onAssegnazioneClick: (ordineLavoroId: number) => void;
    onAssegnazioneDrop: (
        assegnazioneId: number,
        nuovaMacchinaId: number,
        nuovaDataDa: string,
        nuovaDataA: string
    ) => void;
    onAssegnazioneResize: (
        assegnazioneId: number,
        nuovaDataDa: string | null,
        nuovaDataA: string | null
    ) => void;
    onAssegnazioneDelete: (
        assegnazioneId: number,
    ) => void;
    onOrdineLavoroDrop: (
        ordineLavoroId: number,
        macchinaId: number,
        data: string
    ) => void;
}

// ============================================
// CONSTANTS
// ============================================

const ALTEZZA_BARRA = 36;
const PADDING_BARRA = 4;
const ALTEZZA_LANE = ALTEZZA_BARRA + PADDING_BARRA;

// ============================================
// UTILITIES
// ============================================

const generaSettimane = (dataDa: DateTime, numeroSettimane: number): DateTime[][] => {
    const settimane: DateTime[][] = [];
    let lunedi = dataDa.startOf("week");

    for (let s = 0; s < numeroSettimane; s++) {
        const giorni: DateTime[] = [];
        for (let d = 0; d < 6; d++) {
            giorni.push(lunedi.plus({ days: d }));
        }
        settimane.push(giorni);
        lunedi = lunedi.plus({ weeks: 1 });
    }

    return settimane;
};

const getLgSettimana = (numeroSettimane: number): number => {
    switch (numeroSettimane) {
        case 1: return 12;
        case 2: return 6;
        case 3: return 4;
        case 4: return 3;
        case 6: return 2;
        default: return 12;
    }
};

/**
 * Calcola la lane per ogni assegnazione per evitare sovrapposizioni
 */
const calcolaLanes = (assegnazioni: AssegnazioneGantt[]): Map<number, number> => {
    const laneMap = new Map<number, number>();

    if (assegnazioni.length === 0) return laneMap;

    const sorted = [...assegnazioni].sort((a, b) => {
        const dateA = DateTime.fromISO(a.data_da as string);
        const dateB = DateTime.fromISO(b.data_da as string);
        return dateA.toMillis() - dateB.toMillis();
    });

    const lanes: DateTime[] = [];

    sorted.forEach((ass) => {
        const dataInizio = DateTime.fromISO(ass.data_da as string);
        const dataFine = DateTime.fromISO(ass.data_a as string);

        let laneIndex = lanes.findIndex((laneEndDate) => laneEndDate < dataInizio);

        if (laneIndex === -1) {
            laneIndex = lanes.length;
            lanes.push(dataFine);
        } else {
            lanes[laneIndex] = dataFine;
        }

        laneMap.set(ass.id!, laneIndex);
    });

    return laneMap;
};

// ============================================
// COMPONENT
// ============================================

const GanttMacchinaRow = ({
                              macchina,
                              dataDa,
                              numeroSettimane,
                              ordineLavoroSelezionatoId,
                              onAssegnazioneClick,
                              onAssegnazioneDrop,
                              onAssegnazioneResize,
                              onAssegnazioneDelete,
                              onOrdineLavoroDrop,
                          }: GanttMacchinaRowProps) => {
    const theme = useTheme();

    const tecniche = useSelector<RootState, Tecnica[]>(
        (state) => state.anagraficheState?.tecniche?.results || []
    );
    // Selector: prende solo le assegnazioni di questa macchina
    const assegnazioni = useSelector<RootState, AssegnazioneGantt[]>(
        (state) =>
            state.assegnazioniGanttState.assegnazioniList.results.filter(
                (a) => a.macchina === macchina.id
            )
    );

    const settimane = useMemo(
        () => generaSettimane(dataDa, numeroSettimane),
        [dataDa, numeroSettimane]
    );

    const lgSettimana = getLgSettimana(numeroSettimane);

    // Calcola range totale per posizionamento barre
    const dataInizio = dataDa.startOf("week");
    const totaleGiorni = numeroSettimane * 6;

    // Type accettati basati sulle capacità della macchina
    const acceptedTypes = useMemo(
        () => getAcceptedTypes(macchina.tecniche),
        [macchina.tecniche]
    );

    // Calcola le lane per le sovrapposizioni
    const laneMap = useMemo(
        () => calcolaLanes(assegnazioni),
        [assegnazioni]
    );

    // Calcola il numero massimo di lane per l'altezza della riga
    const maxLane = useMemo(() => {
        if (laneMap.size === 0) return 0;
        return Math.max(...Array.from(laneMap.values()));
    }, [laneMap]);

    // Altezza dinamica della riga
    const altezzaRiga = Math.max(60, (maxLane + 1) * ALTEZZA_LANE + 16);

    // Handler per drop su una cella (spostamento barra)
    const handleCellDrop = (giorno: DateTime) => (draggedItem: GanttBarDragItem) => {
        const dataDaOriginale = DateTime.fromISO(draggedItem.dataDa);
        const dataAOriginale = DateTime.fromISO(draggedItem.dataA);
        const durataGiorni = dataAOriginale.diff(dataDaOriginale, "days").days;

        const nuovaDataDa = giorno.toFormat("yyyy-MM-dd");
        const nuovaDataA = giorno.plus({ days: durataGiorni }).toFormat("yyyy-MM-dd");

        onAssegnazioneDrop(
            draggedItem.assegnazioneId,
            macchina.id,
            nuovaDataDa,
            nuovaDataA
        );
    };

    // Handler per resize
    const handleCellResize = (draggedItem: GanttBarDragItem, nuovaData: string) => {
        if (draggedItem.resizeEdge === "left") {
            onAssegnazioneResize(draggedItem.assegnazioneId, nuovaData, null);
        } else if (draggedItem.resizeEdge === "right") {
            onAssegnazioneResize(draggedItem.assegnazioneId, null, nuovaData);
        }
    };

    // Handler per drop OdL
    const handleOrdineLavoroDrop = (ordineLavoroId: number, macchinaId: number, data: string) => {
        onOrdineLavoroDrop(ordineLavoroId, macchinaId, data);
    };

    return (
        <Box
            sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                minHeight: altezzaRiga,
            }}
        >
            <Grid container sx={{ height: "100%", minHeight: altezzaRiga }}>
                {/* Sidebar macchina */}
                <Grid
                    size={{ xs: 2 }}
                    sx={{
                        backgroundColor: theme.palette.grey[50],
                        borderRight: `1px solid ${theme.palette.divider}`,
                        p: 1,
                    }}
                >
                    <Tooltip title={`€ ${macchina.capacita_giornaliera?.toLocaleString("it-IT") || 0}/giorno`}>
                        <Typography variant="body2" fontWeight="bold">
                            {macchina.codice}
                        </Typography>
                    </Tooltip>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                        {macchina.tecniche?.map((tecnicaId) => (
                                <Chip
                                    key={tecnicaId}

                                    label={tecniche.find(t=>t.id==tecnicaId)?.nome}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: 10, height: 20 }}
                                />
                        ))}
                    </Stack>
                </Grid>

                {/* Area settimane con celle droppable */}
                <Grid size={{ xs: 10 }} sx={{ position: "relative", minHeight: altezzaRiga }}>
                    {/* Griglia giorni (celle droppable) */}
                    <Grid container sx={{ height: "100%", minHeight: altezzaRiga }}>
                        {settimane.map((giorni, indexSettimana) => (
                            <Grid
                                key={indexSettimana}
                                size={{ xs: lgSettimana }}
                            >
                                <Grid container sx={{ height: "100%", minHeight: altezzaRiga }}>
                                    {giorni.map((giorno) => (
                                        <Grid
                                            key={giorno.toISODate()}
                                            size={{ xs: 2 }}
                                            sx={{ minHeight: altezzaRiga, display: "flex", flexDirection: "column" }}
                                        >
                                            <GanttDropCell
                                                macchinaId={macchina.id}
                                                giorno={giorno}
                                                acceptedTypes={acceptedTypes}
                                                onDrop={handleCellDrop(giorno)}
                                                onResize={handleCellResize}
                                                onOrdineLavoroDrop={handleOrdineLavoroDrop}
                                                minHeight={altezzaRiga}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Barre assegnazioni (overlay) */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            pointerEvents: "none",
                        }}
                    >
                        {assegnazioni.map((assegnazione) => (
                            <GanttBar
                                key={assegnazione.id}
                                assegnazione={assegnazione}
                                dataInizio={dataInizio}
                                totaleGiorni={totaleGiorni}
                                lane={laneMap.get(assegnazione.id!) || 0}
                                isSelected={assegnazione.ordine_di_lavoro === ordineLavoroSelezionatoId}
                                onClick={onAssegnazioneClick}
                                onAssegnazioneDelete={onAssegnazioneDelete}
                            />
                        ))}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default GanttMacchinaRow;